import { BoundaryChangeType, JobFlags, JobsPriority, IAirtableAttachment } from "../../utilities/types";
import { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import { BaseDataModel } from "./base";
import type { Deal } from "./Deal";
import { IS_DEV, IS_DEV_SENTRY } from "../../utilities/constants";
import { generateUUID } from "../../utilities/utils";
import { handleKMZ, Mission } from "@rogoag/shared-ts";
import { FileDownloader } from "../../utilities/downloadFile";

export enum JobStatusNumber {
	Unready = 0,
	Ready = 1,
	Sampled = 2,
	DropShipped = 3,
	CSVUnsent = 4,
	Completed = 7,
}

export interface ErrorNote {
	source: string;
	note: string;
}

const exeMissionCache = new WeakMap<Job, Mission>();
const intMissionCache = new WeakMap<Job, Mission>();

async function loadMission(
	job: Job,
	attachmentKey: "exeMission" | "intMission",
	intOrExe: "INT" | "EXE",
): Promise<Mission | undefined> {
	const attachments = job.attachments[attachmentKey];
	if (!attachments || attachments.length === 0) return undefined;
	const attachment = attachments[0];
	if (!attachment.url) return undefined;

	const isKmz = /\.kmz$/i.test(attachment.filename);
	let kml: string | undefined;

	if (isKmz) {
		const kmz = await FileDownloader.loadAttachment<Buffer>(attachment, job.id, "arraybuffer");
		if (!kmz) return undefined;
		kml = await handleKMZ(kmz, attachment.filename);
	} else {
		kml = await FileDownloader.loadAttachment<string>(attachment, job.id, "text");
	}

	if (!kml) return undefined;

	const filename = isKmz ? attachment.filename.replace(/\.kmz$/i, ".kml") : attachment.filename;
	try {
		return await Mission.load({ data: kml, filename }, {}, intOrExe);
	} catch (err) {
		console.warn(`Failed to parse ${intOrExe} mission:`, err);
		return undefined;
	}
}

export class Job extends BaseDataModel {
	readonly name!: string; // formula
	readonly edits!: string; // formula

	readonly status!: string; // formula
	readonly statusNumber!: JobStatusNumber; // formula
	sampleChanges!: string;
	boundaryAcres!: number;
	lastCrop?: string;
	nextCrop?: string;
	yieldGoal!: string;
	numberOfSamples?: number;

	fieldId?: string;
	readonly fieldName!: string; // GET: formula
	eventId!: string;

	jobFlags!: JobFlags[];
	dataSent!: string[];
	csvSentToLab!: boolean;
	boundaryChangeType?: BoundaryChangeType;
	boundaryChangeNotes?: string;
	fieldPriority!: JobsPriority;

	readonly testPackage!: string; // lookup from Deal
	readonly season!: string; // lookup from Deal
	readonly depth!: number; // lookup from Deal
	readonly density?: number; // lookup from Deal
	readonly frequencyOfMicros?: number; // lookup from Deal
	readonly labName?: string; // lookup from Deal
	microsList?: string;
	isBillingApproved!: boolean;
	isMarkedReady!: boolean;
	isBoundaryChanged!: boolean;
	isBoundaryApproved!: boolean;
	isPointsChanged!: boolean;
	isTestJob?: boolean;
	isPointsAttached!: boolean;
	isReadyToSample!: boolean;

	dateCreated!: Date;
	dateFileLastEdited?: Date;
	dateSampled?: Date;
	dateSampledManualOverride?: Date;
	dateShipped?: Date;
	dateLabResultsSent?: Date;
	dateFieldReady?: Date;
	dateFieldReadyEstimated?: Date;

	submissionNotes?: string;
	submitterEmail?: string;
	submitterName?: string;

	labSubmittalId!: string;
	readonly labAccountNumber?: string;
	labSubmissionCode?: string;
	labInstructions!: string;

	readonly attachments!: {
		pointsSHP?: IAirtableAttachment[];
		boundarySHP?: IAirtableAttachment[];
		boundaryGeoJSON?: IAirtableAttachment[];
		zonesSHP?: IAirtableAttachment[];
		executedPointsSHP?: IAirtableAttachment[];
		executedBoundarySHP?: IAirtableAttachment[];
		executedPointsBoundarySHP?: IAirtableAttachment[];
		labPDF?: IAirtableAttachment[];
		labCSV?: IAirtableAttachment[];
		labResults?: IAirtableAttachment[];
		fieldNotes?: IAirtableAttachment[];
		exeMission?: IAirtableAttachment[];
		intMission?: IAirtableAttachment[];
	};

	/**
	 * Contains the file data itself. Primarily intended for creating new jobs.
	 */
	files!: {
		pointsSHP?: File[];
		pointsGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
		boundarySHP?: File[];
		boundaryGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
		zonesSHP?: File[];
		zonesGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
		executedPointsSHP?: File[];
		executedBoundarySHP?: File[];
		executedPointsBoundarySHP?: File[];
		labPDF?: File[];
		labCSV?: File[];
		labResults?: File[];
		fieldNotes?: File[];
	};

	readonly companyName!: string; // lookup from Company
	readonly farmName?: string; // formula
	readonly growerName!: string; // lookup from Grower
	growerPhoneNumber?: string;

	branchIds!: string[];
	regionIds!: string[];
	dealIds!: string[];
	growerIds!: string[];
	salespersonIds!: string[];
	shiftId?: string;

	apiId?: string; // also used for creating new jobs
	errorNotes!: ErrorNote[];
	url!: string;

	// used for creating new jobs
	importFieldName?: string;
	importDepth?: number;
	importDensity?: number;
	importLabName?: string;
	importFarmName?: string;
	importGrowerName?: string;

	externalId?: string;
	externalTestPackageId?: string;
	externalFrequencyOfMicros?: number;
	externalLabId?: string;
	externalFarmId?: string;
	externalGrower?: {
		id: string;
		name: string;
	};
	newSalesperson?: {
		name: string;
		phone: string;
	};
	collectBoundary?: boolean;

	exeMission = async (): Promise<Mission | undefined> => {
		const cached = exeMissionCache.get(this);
		if (cached) return cached;
		const mission = await loadMission(this, "exeMission", "EXE");
		if (mission) exeMissionCache.set(this, mission);
		return mission;
	};

	intMission = async (): Promise<Mission | undefined> => {
		const cached = intMissionCache.get(this);
		if (cached) return cached;
		const mission = await loadMission(this, "intMission", "INT");
		if (mission) intMissionCache.set(this, mission);
		return mission;
	};

	static defaultForImport(
		fieldName?: string,
		depth?: number,
		density?: number,
		growerName?: string,
		frequencyOfMicros?: number,
		testPackage?: string,
		farmName?: string,
		labName?: string,
	): Job {
		const defaults = {
			id: generateUUID(),
			name: "",
			fieldName: fieldName ?? "",
			importFieldName: fieldName ?? "",
			growerName: growerName ?? "",
			importGrowerName: growerName ?? "",
			depth: depth ?? 0,
			importDepth: depth ?? 0,
			farmName: farmName ?? "",
			importFarmName: farmName ?? "",
			labName: labName ?? "",
			importLabName: labName ?? "",
			frequencyOfMicros: frequencyOfMicros ?? undefined,
			externalFrequencyOfMicros: frequencyOfMicros ?? undefined,
			testPackage: testPackage ?? "",
			externalTestPackageId: testPackage ?? "",
			branchIds: [],
			regionIds: [],
			boundaryChangeType: undefined,
			boundaryChangeNotes: undefined,
			dealIds: [],
			externalId: "",
			fieldId: "",
			fieldPriority: "Regular Turn (default)",
			density: density ?? 0,
			importDensity: density ?? 0,
			growerPhoneNumber: "",
			jobFlags: [],
			dataSent: [],
			csvSentToLab: false,
			labSubmissionCode: "",
			isPointsAttached: false,
			isReadyToSample: false,
			labInstructions: "",
			submitterEmail: "",
			submitterName: "",
			eventId: "",
			growerIds: [],
			salespersonIds: [],
			edits: "",
			season: "",
			sampleChanges: "",
			url: "",
			labSubmittalId: "",
			companyName: "",
			boundaryAcres: 0,
			status: "Pending",
			statusNumber: 0,
			isBillingApproved: false,
			isBoundaryApproved: false,
			isBoundaryChanged: false,
			isMarkedReady: false,
			isPointsChanged: false,
			isTestJob: IS_DEV || IS_DEV_SENTRY,
			dateCreated: new Date(),
			lastCrop: undefined,
			nextCrop: undefined,
			yieldGoal: "",
			numberOfSamples: undefined,
			errorNotes: [],
			attachments: {
				pointsSHP: [],
				boundarySHP: [],
				boundaryGeoJSON: [],
				zonesSHP: [],
				executedPointsSHP: [],
				executedBoundarySHP: [],
				executedPointsBoundarySHP: [],
				labPDF: [],
				labCSV: [],
				labResults: [],
				fieldNotes: [],
			},
			files: {
				pointsSHP: undefined,
				pointsGeoJSON: undefined,
				boundarySHP: undefined,
				boundaryGeoJSON: undefined,
				zonesSHP: undefined,
				zonesGeoJSON: undefined,
				executedPointsSHP: undefined,
				executedBoundarySHP: undefined,
				executedPointsBoundarySHP: undefined,
				labPDF: undefined,
				labCSV: undefined,
				labResults: undefined,
				fieldNotes: undefined,
			},
		};
		return Object.assign(new Job(), defaults);
	}

	/**
	 * Apply deal fields to a job (mutates the job in place)
	 */
	static updateFromDeal(job: Job, deal: Deal): void {
		job.importDensity = deal.density;
		job.importDepth = deal.depth;
		const testPackage = deal.testPackage || "No test package specified";
		job.externalTestPackageId = Array.isArray(testPackage) ? testPackage.join("") : testPackage;
		job.externalFrequencyOfMicros = deal.frequencyOfMicros ?? 0;
	}
}
